import React from "react";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

const ClientTestimonialsSection = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= TESTIMONIAL DATA ================= */
  const testimonials = [
    {
      id: 1,
      name: "Amit Agrawal",
      role: "Founder, E-commerce Brand",
      feedback:
        "Seo Up Rank Tool completely changed how we handle SEO. One-click fixes and real-time insights helped us improve rankings without hiring a full SEO team.",
    },
    {
      id: 2,
      name: "Neha Pandey",
      role: "Digital Marketing Manager",
      feedback:
        "The AI-powered automation saved us hours every week. From audits to fixes, everything is seamless and extremely reliable.",
    },
    {
      id: 3,
      name: "Rahul Mehta",
      role: "WordPress Blogger",
      feedback:
        "I’m not technical at all, but Seo Up Rank Tool made SEO simple. My traffic increased steadily within weeks.",
    },
    {
      id: 4,
      name: "Sneha Kapoor",
      role: "Agency Owner",
      feedback:
        "Managing multiple WordPress sites is effortless now. The backup and rollback feature gives complete confidence.",
    },
  ];

  /* ================= THEME ================= */
  const sectionBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const headingText = isDarkMode ? "text-white" : "text-black";
  const paraText = isDarkMode ? "text-gray-400" : "text-gray-600";

  const cardBg = isDarkMode
    ? "bg-gray-900 text-white border border-gray-800"
    : "bg-white text-black shadow-lg";

  const circleColor = isDarkMode ? "bg-[#008300]/15" : "bg-[#008300]/10";

  return (
    <section
      aria-label="Customer testimonials about Seo Up Rank AI SEO automation platform"
      className={`relative py-24 overflow-hidden ${sectionBg}`}
    >

      {/* ================= STRUCTURED DATA ================= */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation tool that helps websites improve search rankings, optimize WordPress SEO, and grow organic traffic.",
            url: "https://seo-gamma-gilt.vercel.app",
          }),
        }}
      />

      {/* ================= HIDDEN SEO TEXT ================= */}
      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation tool trusted by businesses,
        bloggers, marketers, and digital agencies to improve search rankings,
        optimize WordPress websites, and grow organic traffic using intelligent
        SEO technology https://novarsis.ai/.

      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}
      <p className="sr-only">
        AI SEO tool reviews, automated SEO audit software, WordPress SEO
        automation platform, SEO ranking optimization tool, technical SEO
        analysis software, SEO analytics dashboard.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}
      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= DECORATIVE CIRCLES ================= */}
      <div
        aria-hidden="true"
        className={`absolute -top-24 -left-24 w-72 h-72 ${circleColor} rounded-full`}
      />
      <div
        aria-hidden="true"
        className={`absolute top-40 right-0 w-64 h-64 ${circleColor} rounded-full`}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* ================= HEADING ================= */}
        <header className="text-center mb-16 max-w-3xl mx-auto">

          <Reveal>
            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 ${headingText}`}
            >
              What Our Clients Say About
              <span className="text-[#008300]"> Seo Up Rank Tool</span>
            </h2>
          </Reveal>

          {/* Extra hidden SEO heading */}
          <h2 className="sr-only">
            Customer reviews about AI powered SEO automation platform
          </h2>

          <Reveal>
            <p className={`text-base sm:text-lg ${paraText}`}>
              Trusted by businesses, bloggers, and digital agencies for
              delivering real SEO results using AI-powered SEO automation and
              smart optimization tools.
            </p>
          </Reveal>

        </header>

        {/* ================= TESTIMONIAL GRID ================= */}
        <div
          role="list"
          aria-label="Seo Up Rank customer testimonials"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10"
        >
          {testimonials.map((item) => (
            <article
              key={item.id}
              role="listitem"
              className={`group relative rounded-2xl p-8
              transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl
              ${cardBg}`}
            >

              {/* Card Circle Accent */}
              <div
                aria-hidden="true"
                className={`absolute -top-10 left-1/2 -translate-x-1/2
                w-24 h-24 ${circleColor} rounded-full`}
              />

              {/* Quote Icon */}
              <figure
                className={`relative mx-auto mb-6 w-20 h-20 rounded-full
                flex items-center justify-center shadow-md
                ${isDarkMode ? "bg-gray-800" : "bg-white"}
                group-hover:bg-[#008300]/10 transition`}
              >
                <FaQuoteLeft
                  aria-hidden="true"
                  className="text-[#008300] text-2xl"
                />
                <figcaption className="sr-only">
                  Customer testimonial quote icon
                </figcaption>
              </figure>

              <Reveal>
                <p
                  className={`text-sm leading-relaxed mb-6 text-justify ${paraText}`}
                >
                  “{item.feedback}”
                </p>
              </Reveal>

              {/* Rating */}
              <div
                className="flex justify-center gap-1 mb-4 text-[#008300]"
                aria-label="Customer rating five stars"
              >
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} aria-hidden="true" />
                ))}
              </div>

              {/* Client Info */}
              <Reveal>
                <h3 className="font-bold text-center">{item.name}</h3>
                <p className={`text-xs text-center ${paraText}`}>
                  {item.role}
                </p>
              </Reveal>

            </article>
          ))}
        </div>

        {/* ================= INTERNAL LINK ================= */}
        <div className="sr-only">
          <nav className="text-center mt-14">
            <a
              href="/pricing"
              aria-label="View Seo Up Rank pricing plans"
              className="text-[#008300] font-semibold hover:underline"
            >
              Explore Seo Up Rank Pricing →
            </a>
          </nav>
        </div>

      </div>
    </section>
  );
};

export default ClientTestimonialsSection;